import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Plus, Maximize, Check, Wand2 } from 'lucide-react';
import FlowSelector from './FlowSelector';
import URLInputWithLibrary from './URLInputWithLibrary';
import useImage from 'use-image';

interface CompositePhotoInstruction {
    flowId?: number;
    x: number;
    y: number;
    width: number;
    height: number;
}

interface CompositeDesignerProps {
    isOpen: boolean;
    onClose: () => void;
    width: number;
    height: number;
    backgroundUrl?: string;
    overlayUrl?: string;
    photoInstructions: CompositePhotoInstruction[];
    expectedPhotoCount: number;
    onSave: (
        instructions: CompositePhotoInstruction[],
        expectedCount: number,
        backgroundUrl?: string,
        overlayUrl?: string,
    ) => void;
}

interface DragState {
    isDragging: boolean;
    photoIndex: number;
    dragType: 'move' | 'resize';
    startX: number;
    startY: number;
    initialPhoto: CompositePhotoInstruction;
}

export default function CompositeDesigner({
    isOpen,
    onClose,
    width: initialWidth = 600,
    height: initialHeight = 1800,
    backgroundUrl,
    overlayUrl,
    photoInstructions: initialInstructions = [],
    expectedPhotoCount: initialExpectedCount = 0,
    onSave,
}: CompositeDesignerProps) {
    const [canvasWidth, setCanvasWidth] = useState(initialWidth);
    const [canvasHeight, setCanvasHeight] = useState(initialHeight);
    const [localBackgroundUrl, setLocalBackgroundUrl] = useState(
        backgroundUrl || '',
    );
    const [localOverlayUrl, setLocalOverlayUrl] = useState(overlayUrl || '');

    // Use proper image loading hooks with error handling
    const [backgroundImage, backgroundStatus] = useImage(
        localBackgroundUrl || '',
        'anonymous',
    );
    const [overlayImage, overlayStatus] = useImage(
        localOverlayUrl || '',
        'anonymous',
    );

    // Log image loading status for debugging
    useEffect(() => {
        if (localBackgroundUrl) {
            console.log(
                'Background image status:',
                backgroundStatus,
                localBackgroundUrl,
            );
        }
    }, [backgroundStatus, localBackgroundUrl]);

    useEffect(() => {
        if (localOverlayUrl) {
            console.log(
                'Overlay image status:',
                overlayStatus,
                localOverlayUrl,
            );
        }
    }, [overlayStatus, localOverlayUrl]);

    // Custom overlay URL setter
    const handleOverlayUrlChange = useCallback((newUrl: string) => {
        setLocalOverlayUrl(newUrl);
    }, []);

    // Resize canvas when overlay URL prop changes (e.g., when opening designer with existing overlay)
    const prevOverlayUrlRef = useRef<string>();
    useEffect(() => {
        if (overlayUrl && overlayUrl !== prevOverlayUrlRef.current) {
            setLocalOverlayUrl(overlayUrl);
            prevOverlayUrlRef.current = overlayUrl;
        }
    }, [overlayUrl]); // Only depend on overlayUrl prop to avoid loops

    // Resize canvas when overlay image loads
    useEffect(() => {
        if (overlayImage && overlayStatus === 'loaded') {
            console.log(
                `Resizing canvas to overlay dimensions: ${overlayImage.width}x${overlayImage.height}`,
            );
            // Ensure canvas dimensions exactly match the overlay image
            setCanvasWidth(overlayImage.width);
            setCanvasHeight(overlayImage.height);
        }
    }, [overlayImage, overlayStatus]);
    const [photoInstructions, setPhotoInstructions] =
        useState<CompositePhotoInstruction[]>(initialInstructions);
    const [expectedPhotoCount, setExpectedPhotoCount] =
        useState(initialExpectedCount);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number | null>(
        null,
    );
    const [dragState, setDragState] = useState<DragState | null>(null);

    // Local opacity controls (not persisted)
    const [backgroundOpacity, setBackgroundOpacity] = useState(1);
    const [overlayOpacity, setOverlayOpacity] = useState(1);
    const [showBackground, setShowBackground] = useState(true);
    const [showOverlay, setShowOverlay] = useState(true);

    const canvasRef = useRef<HTMLDivElement>(null);
    const canvasContainerRef = useRef<HTMLDivElement>(null);
    const backgroundImageRef = useRef<HTMLImageElement>(null);
    const overlayImageRef = useRef<HTMLImageElement>(null);

    // Responsive canvas sizing - calculate available space and scale accordingly
    const [canvasContainerSize, setCanvasContainerSize] = useState({
        width: 800,
        height: 600,
    });

    // Calculate display scale based on available container space
    const calculateDisplayScale = useCallback(() => {
        // Reserve some padding for the container
        const availableWidth = canvasContainerSize.width - 48; // 24px padding on each side
        const availableHeight = canvasContainerSize.height - 48; // 24px padding on top/bottom

        // Calculate scale to fit within available space, but don't scale up beyond 100%
        const scaleX = availableWidth / canvasWidth;
        const scaleY = availableHeight / canvasHeight;
        const scale = Math.min(scaleX, scaleY, 1);

        return Math.max(0.1, scale); // Minimum 10% scale to ensure visibility
    }, [canvasWidth, canvasHeight, canvasContainerSize]);

    const DISPLAY_SCALE = calculateDisplayScale();
    const displayWidth = Math.round(canvasWidth * DISPLAY_SCALE);
    const displayHeight = Math.round(canvasHeight * DISPLAY_SCALE);

    // Monitor canvas container size for responsive scaling
    useEffect(() => {
        const container = canvasContainerRef.current;
        if (!container) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setCanvasContainerSize({ width, height });
            }
        });

        resizeObserver.observe(container);

        // Initial size measurement
        const rect = container.getBoundingClientRect();
        setCanvasContainerSize({ width: rect.width, height: rect.height });

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    // Debug logging for canvas sizing
    useEffect(() => {
        if (overlayImage && overlayStatus === 'loaded') {
            console.log('Canvas sizing debug:', {
                overlayImageSize: `${overlayImage.width}x${overlayImage.height}`,
                canvasSize: `${canvasWidth}x${canvasHeight}`,
                containerSize: `${canvasContainerSize.width}x${canvasContainerSize.height}`,
                displayScale: DISPLAY_SCALE,
                displaySize: `${displayWidth}x${displayHeight}`,
            });
        }
    }, [
        overlayImage,
        overlayStatus,
        canvasWidth,
        canvasHeight,
        canvasContainerSize,
        displayWidth,
        displayHeight,
        DISPLAY_SCALE,
    ]);

    // Add a new photo placeholder
    const addPhoto = useCallback(() => {
        const newPhoto: CompositePhotoInstruction = {
            x: 50,
            y: 50,
            width: 200,
            height: 200,
        };
        setPhotoInstructions((prev) => [...prev, newPhoto]);
        setSelectedPhotoIndex(photoInstructions.length);
        setExpectedPhotoCount((prev) =>
            Math.max(prev, photoInstructions.length + 1),
        );
    }, [photoInstructions.length]);

    // Remove a photo placeholder
    const removePhoto = useCallback((index: number) => {
        setPhotoInstructions((prev) => prev.filter((_, i) => i !== index));
        setSelectedPhotoIndex(null);
        setExpectedPhotoCount((prev) => Math.max(0, prev - 1));
    }, []);

    // Update photo instruction
    const updatePhotoInstruction = useCallback(
        (index: number, updates: Partial<CompositePhotoInstruction>) => {
            setPhotoInstructions((prev) =>
                prev.map((photo, i) =>
                    i === index ? { ...photo, ...updates } : photo,
                ),
            );
        },
        [],
    );

    // Fill canvas with photo
    const fillCanvas = useCallback(
        (index: number) => {
            updatePhotoInstruction(index, {
                x: 0,
                y: 0,
                width: canvasWidth,
                height: canvasHeight,
            });
        },
        [canvasWidth, canvasHeight, updatePhotoInstruction],
    );

    // Handle mouse events for dragging
    const handleMouseDown = useCallback(
        (
            e: React.MouseEvent,
            photoIndex: number,
            dragType: 'move' | 'resize',
        ) => {
            e.preventDefault();
            e.stopPropagation();

            const rect = canvasRef.current?.getBoundingClientRect();
            if (!rect) return;

            const x = (e.clientX - rect.left) / DISPLAY_SCALE;
            const y = (e.clientY - rect.top) / DISPLAY_SCALE;

            setDragState({
                isDragging: true,
                photoIndex,
                dragType,
                startX: x,
                startY: y,
                initialPhoto: photoInstructions[photoIndex],
            });
            setSelectedPhotoIndex(photoIndex);
        },
        [photoInstructions, DISPLAY_SCALE],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!dragState || !canvasRef.current) return;

            const rect = canvasRef.current.getBoundingClientRect();
            const x = (e.clientX - rect.left) / DISPLAY_SCALE;
            const y = (e.clientY - rect.top) / DISPLAY_SCALE;

            const deltaX = x - dragState.startX;
            const deltaY = y - dragState.startY;

            if (dragState.dragType === 'move') {
                const newX = Math.max(
                    0,
                    Math.min(
                        canvasWidth - dragState.initialPhoto.width,
                        dragState.initialPhoto.x + deltaX,
                    ),
                );
                const newY = Math.max(
                    0,
                    Math.min(
                        canvasHeight - dragState.initialPhoto.height,
                        dragState.initialPhoto.y + deltaY,
                    ),
                );

                updatePhotoInstruction(dragState.photoIndex, {
                    x: newX,
                    y: newY,
                });
            } else if (dragState.dragType === 'resize') {
                const newWidth = Math.max(
                    50,
                    dragState.initialPhoto.width + deltaX,
                );
                const newHeight = Math.max(
                    50,
                    dragState.initialPhoto.height + deltaY,
                );

                updatePhotoInstruction(dragState.photoIndex, {
                    width: newWidth,
                    height: newHeight,
                });
            }
        },
        [
            dragState,
            DISPLAY_SCALE,
            canvasWidth,
            canvasHeight,
            updatePhotoInstruction,
        ],
    );

    const handleMouseUp = useCallback(() => {
        setDragState(null);
    }, []);

    // Auto-generate photo placements from overlay transparency
    const autoGenerateFromOverlay = useCallback(async () => {
        console.log('Auto-generate triggered');
        console.log('Local overlay URL:', localOverlayUrl);

        if (!localOverlayUrl) {
            console.log('Missing overlay URL');
            return;
        }

        try {
            // Check if overlay image is loaded
            if (!overlayImage || overlayStatus !== 'loaded') {
                if (overlayStatus === 'failed') {
                    alert(
                        'Failed to load overlay image. Please check the URL and try again.',
                    );
                } else if (overlayStatus === 'loading') {
                    alert(
                        'Please wait for the overlay image to load before generating regions.',
                    );
                } else {
                    alert(
                        'Overlay image not ready. Please try again in a moment.',
                    );
                }
                return;
            }

            console.log(
                'Using loaded overlay image. Dimensions:',
                overlayImage.width,
                'x',
                overlayImage.height,
            );

            // Create a canvas to analyze the overlay image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                console.log('Could not get canvas context');
                return;
            }

            canvas.width = overlayImage.width;
            canvas.height = overlayImage.height;

            console.log('Canvas dimensions:', canvas.width, 'x', canvas.height);

            ctx.drawImage(overlayImage, 0, 0);

            console.log('Drawing image to canvas...');

            const imageData = ctx.getImageData(
                0,
                0,
                canvas.width,
                canvas.height,
            );

            console.log('Image data length:', imageData.data.length);

            // Use connected component analysis for transparent region detection
            console.log(
                'Using connected component analysis for transparent region detection...',
            );

            // Helper types and functions
            type Rect = { x: number; y: number; width: number; height: number };

            interface Options {
                alphaThreshold?: number; // <= this alpha is considered transparent (default 0)
                connectivity4?: boolean; // 4-conn (default) or 8-conn
                minArea?: number; // drop tiny components (default 1)
            }

            /**
             * Returns axis-aligned rectangles that cover all transparent pixels.
             * Each rectangle is the bounding box of a connected transparent component,
             * so opaque "holes" (e.g., logos/text) do not fragment the rectangle.
             */
            const transparentComponentBoxes = (
                imageData: Uint8ClampedArray | Uint8Array,
                width: number,
                height: number,
                opts: Options = {},
            ): Rect[] => {
                const A = imageData;
                const alphaThreshold = opts.alphaThreshold ?? 50; // More lenient than 0
                const use4 = opts.connectivity4 ?? true;
                const minArea =
                    opts.minArea ??
                    Math.max(100, Math.floor(width * height * 0.001)); // Minimum 0.1% of image

                const N = width * height;
                const seen = new Uint8Array(N);

                // Helpers
                const idx = (x: number, y: number) => y * width + x;
                const isTransparent = (i: number) =>
                    A[i * 4 + 3] <= alphaThreshold;

                const rects: Rect[] = [];
                const qx = new Int32Array(N);
                const qy = new Int32Array(N);

                for (let y0 = 0; y0 < height; y0++) {
                    for (let x0 = 0; x0 < width; x0++) {
                        const i0 = idx(x0, y0);
                        if (seen[i0] || !isTransparent(i0)) continue;

                        // BFS flood fill to find one connected transparent component
                        let qh = 0,
                            qt = 0;
                        qx[qt] = x0;
                        qy[qt] = y0;
                        qt++;
                        seen[i0] = 1;

                        let minX = x0,
                            maxX = x0,
                            minY = y0,
                            maxY = y0;
                        let area = 0;

                        while (qh < qt) {
                            const x = qx[qh];
                            const y = qy[qh];
                            qh++;
                            area++;

                            if (x < minX) minX = x;
                            if (x > maxX) maxX = x;
                            if (y < minY) minY = y;
                            if (y > maxY) maxY = y;

                            // 4-connectivity neighbors (N,S,E,W)
                            if (y > 0) {
                                const i = idx(x, y - 1);
                                if (!seen[i] && isTransparent(i)) {
                                    seen[i] = 1;
                                    qx[qt] = x;
                                    qy[qt] = y - 1;
                                    qt++;
                                }
                            }
                            if (y + 1 < height) {
                                const i = idx(x, y + 1);
                                if (!seen[i] && isTransparent(i)) {
                                    seen[i] = 1;
                                    qx[qt] = x;
                                    qy[qt] = y + 1;
                                    qt++;
                                }
                            }
                            if (x > 0) {
                                const i = idx(x - 1, y);
                                if (!seen[i] && isTransparent(i)) {
                                    seen[i] = 1;
                                    qx[qt] = x - 1;
                                    qy[qt] = y;
                                    qt++;
                                }
                            }
                            if (x + 1 < width) {
                                const i = idx(x + 1, y);
                                if (!seen[i] && isTransparent(i)) {
                                    seen[i] = 1;
                                    qx[qt] = x + 1;
                                    qy[qt] = y;
                                    qt++;
                                }
                            }

                            // 8-connectivity diagonals (optional)
                            if (!use4) {
                                if (x > 0 && y > 0) {
                                    const i = idx(x - 1, y - 1);
                                    if (!seen[i] && isTransparent(i)) {
                                        seen[i] = 1;
                                        qx[qt] = x - 1;
                                        qy[qt] = y - 1;
                                        qt++;
                                    }
                                }
                                if (x + 1 < width && y > 0) {
                                    const i = idx(x + 1, y - 1);
                                    if (!seen[i] && isTransparent(i)) {
                                        seen[i] = 1;
                                        qx[qt] = x + 1;
                                        qy[qt] = y - 1;
                                        qt++;
                                    }
                                }
                                if (x > 0 && y + 1 < height) {
                                    const i = idx(x - 1, y + 1);
                                    if (!seen[i] && isTransparent(i)) {
                                        seen[i] = 1;
                                        qx[qt] = x - 1;
                                        qy[qt] = y + 1;
                                        qt++;
                                    }
                                }
                                if (x + 1 < width && y + 1 < height) {
                                    const i = idx(x + 1, y + 1);
                                    if (!seen[i] && isTransparent(i)) {
                                        seen[i] = 1;
                                        qx[qt] = x + 1;
                                        qy[qt] = y + 1;
                                        qt++;
                                    }
                                }
                            }
                        }

                        if (area >= minArea) {
                            // Convert inclusive bounds to [x,y,w,h]
                            rects.push({
                                x: minX,
                                y: minY,
                                width: maxX - minX + 1,
                                height: maxY - minY + 1,
                            });
                        }
                    }
                }

                return rects;
            };

            // Run the connected component analysis
            const rects = transparentComponentBoxes(
                imageData.data,
                canvas.width,
                canvas.height,
                {
                    alphaThreshold: 50,
                    connectivity4: true,
                    minArea: Math.max(
                        100,
                        Math.floor(canvas.width * canvas.height * 0.001),
                    ),
                },
            );

            console.log(
                `Found ${rects.length} connected transparent components`,
            );

            // Additional filtering by aspect ratio and size
            const minWidth = Math.max(80, Math.floor(canvas.width * 0.08));
            const minHeight = Math.max(80, Math.floor(canvas.height * 0.05));

            const filteredRects = rects.filter((r) => {
                // Size filter
                if (r.width < minWidth || r.height < minHeight) return false;

                // Aspect ratio filter - prefer rectangular regions
                const aspectRatio = r.width / r.height;
                if (aspectRatio < 0.2 || aspectRatio > 5.0) return false; // Too thin/tall

                return true;
            });

            console.log(
                `Filtered to ${filteredRects.length} rectangles meeting size and aspect ratio requirements`,
            );

            // Apply padding for better coverage
            const padding = 10; // pixels of padding to ensure good coverage
            const paddedRects = filteredRects.map((r) => ({
                x: Math.max(0, r.x - padding),
                y: Math.max(0, r.y - padding),
                width:
                    Math.min(canvas.width, r.x + r.width + padding) -
                    Math.max(0, r.x - padding),
                height:
                    Math.min(canvas.height, r.y + r.height + padding) -
                    Math.max(0, r.y - padding),
                area: r.width * r.height, // Store original area for sorting
            }));

            // Sort by area (largest first) then by Y position, and take top 4
            paddedRects.sort((a, b) => {
                // Primary sort: by area (largest first)
                const areaDiff = b.area - a.area;
                if (Math.abs(areaDiff) > 1000) return areaDiff; // Significant area difference

                // Secondary sort: by Y position (top to bottom)
                return a.y - b.y;
            });

            const finalRegions = paddedRects.slice(0, 4);

            console.log('Final regions (by area, then top to bottom):');
            finalRegions.forEach((region, i) => {
                console.log(
                    `  ${i + 1}: (${region.x}, ${region.y}) ${region.width}x${
                        region.height
                    } area: ${region.area}`,
                );
            });

            // Convert to photo instructions
            const newInstructions: CompositePhotoInstruction[] =
                finalRegions.map((region) => ({
                    x: (region.x / canvas.width) * canvasWidth,
                    y: (region.y / canvas.height) * canvasHeight,
                    width: (region.width / canvas.width) * canvasWidth,
                    height: (region.height / canvas.height) * canvasHeight,
                }));

            console.log('Generated instructions:', newInstructions);

            setPhotoInstructions(newInstructions);
            setExpectedPhotoCount(newInstructions.length);
            setSelectedPhotoIndex(null);
        } catch (error) {
            console.error('Error analyzing overlay image:', error);

            // Fallback: If CORS fails, generate a simple grid layout
            console.log('Falling back to grid layout due to CORS error');
            const gridSize = 2;
            const photoWidth = (canvasWidth / gridSize) * 0.8;
            const photoHeight = (canvasHeight / gridSize) * 0.8;
            const spacing = Math.min(canvasWidth, canvasHeight) * 0.05;

            const fallbackInstructions: CompositePhotoInstruction[] = [];
            for (let row = 0; row < gridSize; row++) {
                for (let col = 0; col < gridSize; col++) {
                    fallbackInstructions.push({
                        x: col * (canvasWidth / gridSize) + spacing,
                        y: row * (canvasHeight / gridSize) + spacing,
                        width: photoWidth,
                        height: photoHeight,
                    });
                }
            }

            setPhotoInstructions(fallbackInstructions);
            setExpectedPhotoCount(fallbackInstructions.length);
            setSelectedPhotoIndex(null);

            // Show user-friendly message
            alert(
                'Could not analyze overlay image (CORS restriction). Generated a grid layout instead. You can manually adjust the photo positions.',
            );
        }
    }, [
        localOverlayUrl,
        canvasWidth,
        canvasHeight,
        overlayImage,
        overlayStatus,
    ]);

    // Handle save
    const handleSave = useCallback(() => {
        onSave(
            photoInstructions,
            expectedPhotoCount,
            localBackgroundUrl,
            localOverlayUrl,
        );
        onClose();
    }, [
        photoInstructions,
        expectedPhotoCount,
        localBackgroundUrl,
        localOverlayUrl,
        onSave,
        onClose,
    ]);

    // Mouse event listeners
    useEffect(() => {
        if (dragState) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);

            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [dragState, handleMouseMove, handleMouseUp]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-background rounded-lg shadow-xl max-w-7xl max-h-[90vh] w-full mx-4 flex overflow-hidden">
                {/* Left Panel - Controls */}
                <div className="w-80 bg-muted/50 p-6 overflow-y-auto">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold">
                            Composite Designer
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-muted rounded-lg"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Canvas Dimensions */}
                    <div className="mb-6">
                        <h3 className="text-sm font-medium mb-3">
                            Canvas Size
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">
                                    Width
                                </label>
                                <input
                                    type="number"
                                    value={canvasWidth}
                                    onChange={(e) =>
                                        setCanvasWidth(Number(e.target.value))
                                    }
                                    className="w-full px-3 py-2 border border-border rounded-md text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">
                                    Height
                                </label>
                                <input
                                    type="number"
                                    value={canvasHeight}
                                    onChange={(e) =>
                                        setCanvasHeight(Number(e.target.value))
                                    }
                                    className="w-full px-3 py-2 border border-border rounded-md text-sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Expected Photo Count */}

                    {/* Background Image */}
                    <div className="mb-6">
                        <URLInputWithLibrary
                            value={localBackgroundUrl}
                            onChange={setLocalBackgroundUrl}
                            placeholder="Background Image URL"
                            label="Background Image"
                        />
                    </div>

                    {/* Overlay Image */}
                    <div className="mb-6">
                        <URLInputWithLibrary
                            value={localOverlayUrl}
                            onChange={handleOverlayUrlChange}
                            placeholder="Overlay Image URL"
                            label="Overlay Image"
                        />
                    </div>

                    {/* Visibility Controls */}
                    <div className="mb-6">
                        <h3 className="text-sm font-medium mb-3">Visibility</h3>
                        <div className="space-y-3">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={showBackground}
                                    onChange={(e) =>
                                        setShowBackground(e.target.checked)
                                    }
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Background</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    checked={showOverlay}
                                    onChange={(e) =>
                                        setShowOverlay(e.target.checked)
                                    }
                                    className="mr-2"
                                />
                                <span className="text-sm">Show Overlay</span>
                            </label>
                        </div>
                    </div>

                    {/* Opacity Controls */}
                    <div className="mb-6">
                        <h3 className="text-sm font-medium mb-3">Opacity</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">
                                    Background Opacity:{' '}
                                    {Math.round(backgroundOpacity * 100)}%
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={backgroundOpacity}
                                    onChange={(e) =>
                                        setBackgroundOpacity(
                                            Number(e.target.value),
                                        )
                                    }
                                    className="w-full"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-muted-foreground mb-1">
                                    Overlay Opacity:{' '}
                                    {Math.round(overlayOpacity * 100)}%
                                </label>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.1"
                                    value={overlayOpacity}
                                    onChange={(e) =>
                                        setOverlayOpacity(
                                            Number(e.target.value),
                                        )
                                    }
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Center Panel - Canvas */}
                <div
                    ref={canvasContainerRef}
                    className="flex-1 p-6 overflow-auto relative"
                >
                    {/* Scale Indicator */}
                    {DISPLAY_SCALE < 1 && (
                        <div className="absolute top-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs z-10">
                            {Math.round(DISPLAY_SCALE * 100)}% scale
                        </div>
                    )}

                    <div className="flex items-center justify-center min-h-full w-full h-full">
                        <div
                            ref={canvasRef}
                            className="relative border border-border shadow-lg"
                            style={{
                                width: displayWidth,
                                height: displayHeight,
                                backgroundImage: `
                                    linear-gradient(45deg, #f0f0f0 25%, transparent 25%),
                                    linear-gradient(-45deg, #f0f0f0 25%, transparent 25%),
                                    linear-gradient(45deg, transparent 75%, #f0f0f0 75%),
                                    linear-gradient(-45deg, transparent 75%, #f0f0f0 75%)
                                `,
                                backgroundSize: '20px 20px',
                                backgroundPosition:
                                    '0 0, 0 10px, 10px -10px, -10px 0px',
                                backgroundColor: '#ffffff',
                            }}
                        >
                            {/* Background Image */}
                            {localBackgroundUrl &&
                                showBackground &&
                                backgroundImage &&
                                backgroundStatus === 'loaded' && (
                                    <img
                                        ref={backgroundImageRef}
                                        src={localBackgroundUrl}
                                        alt="Background"
                                        className="absolute inset-0 w-full h-full object-cover"
                                        style={{ opacity: backgroundOpacity }}
                                        crossOrigin="anonymous"
                                    />
                                )}
                            {localBackgroundUrl &&
                                showBackground &&
                                backgroundStatus === 'loading' && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-muted">
                                        <div className="text-sm text-muted-foreground">
                                            Loading background...
                                        </div>
                                    </div>
                                )}
                            {localBackgroundUrl &&
                                showBackground &&
                                backgroundStatus === 'failed' && (
                                    <div className="absolute inset-0 flex items-center justify-center bg-red-50">
                                        <div className="text-sm text-red-500">
                                            Failed to load background image
                                        </div>
                                    </div>
                                )}

                            {/* Photo Placeholders */}
                            {photoInstructions.map((photo, index) => (
                                <div
                                    key={index}
                                    className={`absolute border-2 cursor-move ${
                                        selectedPhotoIndex === index
                                            ? 'border-blue-500 bg-blue-100'
                                            : 'border-gray-400 bg-background'
                                    } bg-opacity-50 flex items-center justify-center text-sm font-medium text-foreground`}
                                    style={{
                                        left:
                                            (photo.x / canvasWidth) *
                                            displayWidth,
                                        top:
                                            (photo.y / canvasHeight) *
                                            displayHeight,
                                        width:
                                            (photo.width / canvasWidth) *
                                            displayWidth,
                                        height:
                                            (photo.height / canvasHeight) *
                                            displayHeight,
                                    }}
                                    onMouseDown={(e) => {
                                        e.stopPropagation();
                                        handleMouseDown(e, index, 'move');
                                    }}
                                    onClick={() => setSelectedPhotoIndex(index)}
                                >
                                    <div className="text-center">
                                        <div>Photo {index + 1}</div>
                                        {photo.flowId && (
                                            <div className="text-xs text-muted-foreground">
                                                Flow: {photo.flowId}
                                            </div>
                                        )}
                                    </div>

                                    {/* Fill Canvas Button */}
                                    <button
                                        className="absolute top-1 right-1 w-6 h-6 bg-blue-500 hover:bg-blue-600 text-white rounded flex items-center justify-center"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            fillCanvas(index);
                                        }}
                                        title="Fill Canvas"
                                    >
                                        <Maximize className="w-3 h-3" />
                                    </button>

                                    {/* Resize Handle */}
                                    <div
                                        className="absolute bottom-0 right-0 w-4 h-4 bg-blue-500 cursor-se-resize"
                                        onMouseDown={(e) => {
                                            e.stopPropagation();
                                            handleMouseDown(e, index, 'resize');
                                        }}
                                    />
                                </div>
                            ))}

                            {/* Overlay Image */}
                            {localOverlayUrl &&
                                showOverlay &&
                                overlayImage &&
                                overlayStatus === 'loaded' && (
                                    <img
                                        ref={overlayImageRef}
                                        src={localOverlayUrl}
                                        alt="Overlay"
                                        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                                        style={{
                                            opacity: overlayOpacity,
                                            width: `${displayWidth}px`,
                                            height: `${displayHeight}px`,
                                        }}
                                        crossOrigin="anonymous"
                                    />
                                )}
                            {localOverlayUrl &&
                                showOverlay &&
                                overlayStatus === 'loading' && (
                                    <div className="absolute top-4 left-4 bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm pointer-events-none">
                                        Loading overlay...
                                    </div>
                                )}
                            {localOverlayUrl &&
                                showOverlay &&
                                overlayStatus === 'failed' && (
                                    <div className="absolute top-4 left-4 bg-red-100 text-red-700 px-2 py-1 rounded text-sm pointer-events-none">
                                        Failed to load overlay
                                    </div>
                                )}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Photo List */}
                <div className="w-80 bg-muted/50 border-l border-border p-4 overflow-y-auto flex flex-col">
                    <div className="flex flex-row">
                        <h2 className="text-lg font-semibold text-foreground mb-3 flex-1">
                            Photos ({expectedPhotoCount})
                        </h2>
                        {/* Auto Generate */}
                        <div className="mb-6">
                            <button
                                onClick={autoGenerateFromOverlay}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                            >
                                <Wand2 className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    {/* Photo List */}
                    <div className="space-y-2 mb-4">
                        {photoInstructions.map((photo, index) => (
                            <div
                                key={index}
                                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                    selectedPhotoIndex === index
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-border bg-background hover:bg-muted/50'
                                }`}
                                onClick={() => setSelectedPhotoIndex(index)}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <div>
                                        <div className="text-sm font-medium text-foreground">
                                            Photo {index + 1}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {Math.round(photo.width)} ×{' '}
                                            {Math.round(photo.height)}
                                        </div>
                                    </div>
                                    <div className="flex items-center space-x-1">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removePhoto(index);
                                            }}
                                            className="text-red-500 hover:text-red-700 p-1"
                                            title="Remove photo"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Flow ID Selector */}
                                <div className="mt-2">
                                    <FlowSelector
                                        value={photo.flowId}
                                        onChange={(flowId) =>
                                            updatePhotoInstruction(index, {
                                                flowId: flowId as number,
                                            })
                                        }
                                        placeholder="Select flow..."
                                    />
                                </div>
                            </div>
                        ))}

                        {photoInstructions.length === 0 && (
                            <div className="text-sm text-muted-foreground text-center py-4">
                                No photos added yet
                            </div>
                        )}
                    </div>

                    {/* Add Photo Button */}
                    <button
                        onClick={addPhoto}
                        className="w-full mb-6 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm flex items-center justify-center space-x-2"
                        style={{
                            width: 'calc(100% - 20px)',
                        }}
                    >
                        <Plus className="w-4 h-4" />
                        <span>Add Photo</span>
                    </button>
                    <div className="flex-1" />

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                        <button
                            onClick={handleSave}
                            className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            title="Save Changes"
                            style={{
                                width: 'calc(100% - 16px)',
                            }}
                        >
                            <Check className="w-4 h-4" />
                            <span className="text-sm">Save</span>
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                            title="Cancel"
                            style={{
                                width: 'calc(100% - 16px)',
                            }}
                        >
                            <X className="w-4 h-4" />
                            <span className="text-sm">Cancel</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
